import React from 'react';
import { defineMessages } from 'react-intl';
import _ from 'lodash';
import { loadFavoriteCourses } from '../../../actions/auth';
import Layout from '../../../components/Layout/Layout';
import CourseDetailsContent from './CourseDetailsContent';
import notFound from '../../not-found/index';

import {
  getCourseBySlug,
  getCourseTextNotes,
  getCourseVideoNotes,
} from '../../../actions/courseDetails';
import { getDiscussions } from '../../../actions/discussions';
import { ssrRequest } from '../../../helpers';
import isLoggedIn from '../../../helpers/checkAuth';

const messages = defineMessages({
  coursesTitle: {
    id: 'CourseDetailsContent.coursesTitle',
    defaultMessage: '{name} - Course',
    description: 'Title displayed in Course detail content page',
  },
});

async function action(context) {
  const {
    store,
    params,
    fetch,
    intl: { formatMessage },
    cloudfrontUrl,
    organizationSlug,
  } = context;
  const { courseSlug } = params;

  const { auth } = store.getState();

  const promises = [];

  const getCourseRequest = await ssrRequest({
    getAction: getCourseBySlug.bind(this, courseSlug),
    fetch,
    store,
  });

  if ([403, 404].includes(_.get(getCourseRequest, 'status'))) {
    return notFound(context);
  }

  const { courseDetails } = store.getState();
  const { isAvailable, type: courseType } = courseDetails.course;

  if (isLoggedIn(auth)) {
    if (isAvailable) {
      if (!_.includes(['podcast', 'livestream'], courseType)) {
        promises.push(async () => {
          await store.dispatch({
            type: getCourseTextNotes(courseSlug).types[0],
          });

          try {
            const res = await getCourseTextNotes(courseSlug).promise(fetch);

            const resText = await res.text();

            const json = resText ? JSON.parse(resText) : {};

            if (res.status !== 200) {
              if ([403, 404].includes(res.status)) {
                return notFound(context);
              }

              return store.dispatch({
                error: json,
                type: getCourseTextNotes(courseSlug).types[2],
              });
            }

            const result = {
              status: res.status,
              json,
            };

            return store.dispatch({
              result,
              type: getCourseTextNotes(courseSlug).types[1],
            });
          } catch (error) {
            error.message = `MIDDLEWARE ERROR: ${error?.message}`;
            console.error(error);
            return store.dispatch({
              error,
              type: getCourseTextNotes(courseSlug).types[2],
            });
          }
        });

        promises.push(async () => {
          await store.dispatch({
            type: getCourseVideoNotes(courseSlug).types[0],
          });

          try {
            const res = await getCourseVideoNotes(courseSlug).promise(fetch);

            const resText = await res.text();

            const json = resText ? JSON.parse(resText) : {};

            if (res.status !== 200) {
              if ([403, 404].includes(res.status)) {
                return notFound(context);
              }

              return store.dispatch({
                error: json,
                type: getCourseVideoNotes(courseSlug).types[2],
              });
            }

            const result = {
              status: res.status,
              json,
            };

            return store.dispatch({
              result,
              type: getCourseVideoNotes(courseSlug).types[1],
            });
          } catch (error) {
            error.message = `MIDDLEWARE ERROR: ${error?.message}`;
            console.error(error);
            return store.dispatch({
              error,
              type: getCourseVideoNotes(courseSlug).types[2],
            });
          }
        });
      }

      promises.push(async () => {
        const query = {
          page: 1,
          sort: '-createDate',
          search: '',
          perPage: 50,
          courseId: courseSlug,
        };

        return ssrRequest({
          getAction: getDiscussions.bind(this, query),
          fetch,
          store,
        });
      });
    }

    promises.push(async () => store.dispatch(loadFavoriteCourses(fetch)));
  }

  const requests = await Promise.all(promises.map(promise => promise()));
  const notFoundReq = _.find(requests, item =>
    [403, 404].includes(_.get(item, 'status')),
  );

  if (notFoundReq) {
    return notFound(context);
  }

  const title = formatMessage(messages.coursesTitle, {
    name: _.get(courseDetails, 'course.name', ''),
  });

  const description = _.get(courseDetails, 'course.description', '');
  const keywords = _.get(courseDetails, 'course.tags', '');

  let imageUrl = _.get(courseDetails, 'course.thumbnail.path');
  if (imageUrl) {
    imageUrl = `${cloudfrontUrl}/${imageUrl}`;
  }

  return {
    chunks: ['course-details'],
    title,
    description,
    keywords,
    imageUrl,
    component: (
      <Layout
        context={context}
        tour={
          organizationSlug || _.includes(['podcast', 'livestream'], courseType)
            ? undefined
            : 'courseDetails'
        }
        className="full-height"
      >
        <CourseDetailsContent
          data={{}}
          title={title}
          searchSessionId={context.query?.searchSessionId}
        />
      </Layout>
    ),
  };
}

export default action;
